import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, forkJoin, from, map, Observable, of, shareReplay, Subject, throwError } from 'rxjs';

import {
  GET_MEMBERS,
  GET_FAMILY_DASHBOARD,
  UPSERT_FAMILY_MEMBER,
  DELETE_FAMILY_MEMBER,
  GET_QUALITY_COUNT_BY_QC_NUMBER,
  UPDATE_FAMILY_DETAILS
} from '../../../core/constants/api-endpoints';
import { skipLoading } from '../../../core/interceptors/loading.interceptor';
import { CoreService } from '../../../core/services/core.service';
import { CountyOption, CountyService } from '../../../core/services/county.service';
import { DynamicFormService } from '../../../core/services/dynamic-form.service';
import { IndexedDbService } from '../../../core/services/indexed-db.service';
import { FAMILY_PROFILE_FEATURE } from '../components/family-profile/family-profile.schema';
import { DashboardData, StatCard } from '../../../models/common/dashboard';
import { DashboardStats, GetDashboardStatsRequest } from '../../../models/common/dashboard-stats';
import CommonUtils from '../../../shared/utils/common.utils';
import {
  FamilyMember,
  FamilyMemberRecord,
  toFamilyMember
} from '../../../models/common/family-member';
import { QualityCount, QualityCountResponse, unwrapQualityCount } from '../../../models/common/quality-count';
import { ClubInfo, FamilyProfileResponse } from '../../../models/common/family-profile';
import { Show } from '../../../models/common/show';
import { ShowApi } from '../../../models/common/show-api';
import { DEFAULT_SHOW_ACCENT, DEFAULT_SHOW_CTA, DEFAULT_SHOW_TINT, SHOW_PRESENTATION } from '../../../models/common/show-presentation';
import { BaseSessionService } from '../../../core/services/base-session.service';
import { RosterFamilyDetails } from '../../../shared/components/members-roster/members-roster.model';

/** The FamilyDetails row as GetUserInfo (`user.family`), SaveFamilyDetails and
 *  UpdateFamilyDetails all return it: own columns plus every dynamic field as a
 *  JSON string. */
interface FamilyDetailsRecord {
  id: number;
  familyUuid: string;
  familyName: string;
  email: string;
  formData: string;
  /** Profile columns (address, city, guardian1Name, …) mirrored from `formData`. */
  [column: string]: unknown;
}

/** Record keys that are never a profile field's back-fill. */
const NON_PROFILE_COLUMNS = new Set(['id', 'formdata', 'status', 'emailstatus']);

@Injectable({ providedIn: 'root' })
export class FamilyDashboardService {
  private readonly dynamicForms = inject(DynamicFormService);
  private readonly indexedDb = inject(IndexedDbService);

  private readonly _membersChanged = new Subject<void>();
  /** Fires when the family roster changes, so the shell can refresh setup progress. */
  readonly membersChanged$ = this._membersChanged.asObservable();

  // Cache for profile to avoid duplicate API calls
  private profileCache: FamilyProfileResponse | null = null;
  private profileCacheTimestamp: number = 0;
  private readonly PROFILE_CACHE_DURATION = 1 * 60 * 1000; // 1 minute

  /** In-flight / cached roster, shared by the shell and the members screen. */
  private members$: Observable<FamilyMember[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly session: BaseSessionService,
    private readonly core: CoreService,
    private readonly counties: CountyService
  ) {}

  /** Notify subscribers that a member was added / edited / removed. */
  notifyMembersChanged(): void {
    this.clearMembersCache();
    this._membersChanged.next();
  }

  /** Force the next roster read to refetch, without refreshing the dashboard. */
  clearMembersCache(): void {
    this.members$ = null;
  }

  /** Single-letter avatar initial from the family name. */
  private toInitials(name: string): string {
    return name.trim().charAt(0).toUpperCase();
  }

  /** Capitalizes the first letter of a name; leaves the rest untouched. */
  private capitalize(name: string): string {
    const trimmed = name.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  /** Fetch live dashboard statistics from GetFamilyDashboard. */
  private getStats(): Observable<DashboardStats> {
    const request: GetDashboardStatsRequest = { FamilyPortal: true };
    return this.http
      .post<any>(GET_FAMILY_DASHBOARD('tsp'), request)
      .pipe(
        map((response) => (response?.data ?? {}) as DashboardStats),
        // Keep tiles visible with zeros on error
        catchError(() => of({} as DashboardStats))
      );
  }

  /** Resolves the full dashboard payload for the signed-in family. */
  getDashboard(): Observable<DashboardData> {
    return this.getStats().pipe(
      map((stats) => this.withSetupSteps(this.buildDashboard(stats), stats.familyMemberCount ?? 0, stats.showCount ?? 0))
    );
  }

  /** Setup steps driven by the profile-completed flag + member count + show count. */
  private withSetupSteps(data: DashboardData, memberCount: number, showCount: number): DashboardData {
    const profileComplete = this.session.isProfileCompleted();
    const membersComplete = memberCount > 0;
    const showsComplete = showCount > 0;
    return {
      ...data,
      setupSteps: [
        { label: 'Family Profile', detail: profileComplete ? 'Complete' : 'Not started', complete: profileComplete, route: '/family/profile', icon: 'pi-user' },
        { label: 'Members', detail: membersComplete ? `${memberCount} Registered` : 'Not started', complete: membersComplete, route: '/family/members', icon: 'pi-users' },
        { label: 'Pick a Show', detail: showsComplete ? `${showCount} Enrolled` : 'Not started', complete: showsComplete, route: '/family/shows', icon: 'pi-calendar' }
      ]
    };
  }

  /** 4-H / FFA club options, from the IndexedDB club cache seeded at login. */
  getClubs(): Observable<ClubInfo[]> {
    return from(this.indexedDb.getClubOptions()).pipe(catchError(() => of([] as ClubInfo[])));
  }

  /** Texas counties for the county dropdown. Leads with "N/A". */
  getCounties(): Observable<CountyOption[]> {
    return this.counties.getCounties();
  }

  /** The signed-in family's saved profile, from `User/GetUserInfo` → `data.user.family`
   *  (with caching). Its `formData` is a JSON string that needs to be parsed. */
  getProfile(): Observable<FamilyProfileResponse> {
    if (!this.session.getSessionItem(CoreService.KEY_FAMILY_ID)) {
      return throwError(() => new Error('Family ID not found in session. This endpoint is only available for family role.'));
    }

    const email = this.session.getEmail() ?? this.session.getCurrentUser()?.email;
    if (!email) {
      return throwError(() => new Error('Your sign-in details are incomplete. Please log in again.'));
    }

    // Return cached profile if available and valid
    if (this.profileCache !== null && (Date.now() - this.profileCacheTimestamp) < this.PROFILE_CACHE_DURATION) {
      return of(this.profileCache);
    }

    return forkJoin({
      response: this.core.getUserInfo(email) as Observable<{
        data?: { user?: { family?: FamilyDetailsRecord | null } };
      }>,
      fieldKeys: this.profileFieldKeys()
    }).pipe(
      map(({ response, fieldKeys }) => {
        const family = response?.data?.user?.family;
        if (!family) {
          throw new Error('No family profile found');
        }
        return this.cacheProfile(this.toProfile(family, fieldKeys), fieldKeys);
      })
    );
  }

  /** The profile form's configured fieldKeys; none on failure, which only skips
   *  the column back-fill. */
  private profileFieldKeys(): Observable<string[]> {
    return this.dynamicForms.getFields(FAMILY_PROFILE_FEATURE).pipe(
      map((fields) => fields.map((field) => field.key)),
      catchError(() => of([] as string[]))
    );
  }

  /** Flattens a family record — own columns plus the dynamic fields buried in the
   *  `formData` JSON string — into the profile the UI works with. Both
   *  GetUserInfo and UpdateFamilyDetails return this same record shape. */
  private toProfile(record: FamilyDetailsRecord, fieldKeys: string[]): FamilyProfileResponse {
    let formData: Record<string, unknown>;
    try {
      formData = this.toFieldKeyCase(record.formData ? JSON.parse(record.formData) : {}, fieldKeys);
    } catch (e) {
      console.error('[family-profile] Failed to parse formData:', e);
      throw new Error('Invalid profile data format');
    }

    return {
      ...this.columnBackfill(record, formData, fieldKeys),
      ...formData,
      familyDetailsId: record.id,
      familyUuid: record.familyUuid,
      familyName: (formData['familyName'] as string) || record.familyName,
      email: (formData['email'] as string) || record.email
    };
  }

  /** Renames saved keys to their field key's casing. Older rows hold e.g.
   *  `relationShipToFamilyMember` for the `relationshipToFamilyMember` field, and a
   *  mis-cased key never reaches its form control. */
  private toFieldKeyCase(formData: Record<string, unknown>, fieldKeys: string[]): Record<string, unknown> {
    const byLower = new Map(fieldKeys.map((key) => [key.toLowerCase(), key]));
    return Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [byLower.get(key.toLowerCase()) ?? key, value])
    );
  }

  /**
   * Column values for fields `formData` has lost. Educator/admin flows used to
   * update the family with only `{ familyUuid, familyName }`, which replaced
   * `formData` while the columns kept every value. Written under the field's own
   * key: the API reads keys case-insensitively and takes the first match, so a
   * differently-cased duplicate would override the user's next edit.
   */
  private columnBackfill(
    record: FamilyDetailsRecord,
    formData: Record<string, unknown>,
    fieldKeys: string[]
  ): Record<string, unknown> {
    const saved = new Set(Object.keys(formData).map((key) => key.toLowerCase()));
    const columns = new Map(
      Object.keys(record)
        .filter((key) => !NON_PROFILE_COLUMNS.has(key.toLowerCase()))
        .map((key) => [key.toLowerCase(), record[key]])
    );

    // A key formData holds as null was cleared on purpose, so only missing ones are filled.
    return Object.fromEntries(
      fieldKeys
        .filter((key) => !saved.has(key.toLowerCase()) && columns.get(key.toLowerCase()) != null)
        .map((key) => [key, columns.get(key.toLowerCase())])
    );
  }

  /** A profile read without the field keys has skipped the column back-fill, so
   *  it is served once but not cached — the next read retries the keys. */
  private cacheProfile(profile: FamilyProfileResponse, fieldKeys: string[]): FamilyProfileResponse {
    if (fieldKeys.length === 0) {
      return profile;
    }
    this.profileCache = profile;
    this.profileCacheTimestamp = Date.now();
    return profile;
  }

  /** Name / email of the signed-in family, for the read-only pair on the members
   *  roster's add/edit drawer. */
  getFamilyDetails(): Observable<RosterFamilyDetails> {
    return this.getProfile().pipe(
      map((profile) => ({
        familyName: profile?.familyName ?? null,
        familyEmail: profile?.email ?? null
      }))
    );
  }

  /** Clears the profile cache - useful when profile is updated. */
  clearProfileCache(): void {
    this.profileCache = null;
    this.profileCacheTimestamp = 0;
  }

  /** Persists profile edits via the dynamic UpdateFamilyDetails API.
   *  Accepts raw form values as fieldKey: value pairs (no typed model). */
  updateProfile(formValues: Record<string, any>): Observable<FamilyProfileResponse> {
    const familyUuid = this.session.getSessionItem(CoreService.KEY_FAMILY_ID);
    if (!familyUuid) {
      return throwError(() => new Error('Family ID not found in session. This endpoint is only available for family role.'));
    }

    // Raw JSON payload — dynamic field key/value pairs. The record-level keys go
    // last so a stale `url` carried in the saved form data can't win.
    const payload = {
      ...formValues,
      url: window.location.origin,
      familyUuid
    };

    // The API echoes the saved family record (dynamic fields still inside the
    // `formData` string), so flatten it exactly like getProfile() does.
    return forkJoin({
      response: this.http.post<{ statusCode: number; message: string | null; data: FamilyDetailsRecord }>(
        UPDATE_FAMILY_DETAILS('tsp'),
        payload
      ),
      fieldKeys: this.profileFieldKeys()
    }).pipe(
      map(({ response, fieldKeys }) => this.cacheProfile(this.toProfile(response.data, fieldKeys), fieldKeys))
    );
  }



  /** Gets exhibitors (members) via the Exhibitor API.
   *  Shared + replayed so concurrent callers (shell, members screen) hit the API once;
   *  `notifyMembersChanged()` clears it. */
  getMembersFromExhibitor(search?: string): Observable<FamilyMember[]> {
    const familyUuid = this.session.getSessionItem(CoreService.KEY_FAMILY_ID);
    if (!familyUuid) {
      return throwError(() => new Error('Family ID not found in session.'));
    }

    // Bypass cache when searching to get fresh results from the API
    if (search) {
      return this.http
        .post<{ data: FamilyMemberRecord[] | null }>(GET_MEMBERS('tsp'), {
          familyUuid,
          ExhibitorName: search
        })
        .pipe(
          map((res) => {
            const rows = res?.data ?? [];
            return Array.isArray(rows)
              ? rows.filter((row) => row.isActive !== false).map(toFamilyMember)
              : [];
          })
        );
    }

    // Use cached results when no search term
    if (this.members$) {
      return this.members$;
    }

    this.members$ = this.http
      .post<{ data: FamilyMemberRecord[] | null }>(GET_MEMBERS('tsp'), { familyUuid })
      .pipe(
        map((res) => {
          const rows = res?.data ?? [];
          return Array.isArray(rows)
            ? rows.filter((row) => row.isActive !== false).map(toFamilyMember)
            : [];
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((err: HttpErrorResponse) => {
          // Drop the shared replay so the next call retries instead of
          // replaying the failure.
          this.members$ = null;
          return throwError(() => err);
        })
      );

    return this.members$;
  }

  /** Creates or updates a member via the UpsertFamilyMembers API. The form's
   *  value bag travels as-is — the API keys off the same fieldKeys and stores
   *  the body verbatim as `formData`. A `familyMemberUuid` makes it an update. */
  saveExhibitor(values: Record<string, unknown>, memberId?: string): Observable<FamilyMember> {
    const familyUuid = this.session.getSessionItem(CoreService.KEY_FAMILY_ID);
    if (!familyUuid) {
      return throwError(() => new Error('Family ID not found in session.'));
    }

    const request = {
      ...values,
      familyUuid,
      ...(memberId ? { familyMemberUuid: memberId } : {})
    };

    // Roster is SA-scoped regardless of which portal the user came from, so this
    // call pins its own abbreviation instead of taking the session's.
    return this.http
      .post<{ data: FamilyMemberRecord }>(UPSERT_FAMILY_MEMBER('tsp'), request)
      .pipe(
        map((res) => {
          this.members$ = null;
          return toFamilyMember(res.data);
        })
      );
  }

  /** Deletes a family member via the DeleteFamilyMember API. */
  deleteExhibitorMember(familyMemberUuid: string): Observable<any> {
    const request = { familyMemberUuid };
    return this.http
      .post<any>(DELETE_FAMILY_MEMBER('tsp'), request)
      .pipe(
        map((response) => {
          // Check the response's statusCode (API returns HTTP 200 with statusCode in body)
          if (response?.statusCode && response.statusCode !== 200) {
            // Return the error response so the component can handle it
            throw response;
          }
          this.members$ = null;
          return response;
        })
      );
  }

  /** Shows open to the signed-in family on the "Pick a Show" screen.
   *  Now fetches from GetShows() and maps showCode → UI presentation. */
  getShows(): Observable<Show[]> {
    return this.core.getShows({}).pipe(
      map((response) =>
        (response?.data ?? [])
          .filter((show) => show.isShowScheduleActive === true)
          .map((show) => this.mapShowDetailsToShow(show))
      ),
      catchError(() => of([] as Show[]))
    );
  }

  /** Map a GetShows row → Show card model. The showCode drives the route,
   *  and the full response will be stored in session on click. */
  private mapShowDetailsToShow(details: ShowApi): Show {
    const code = (details.showCode ?? '').toLowerCase();

    // Branding per show code; a code with no preset falls back to a plain tile.
    const preset = SHOW_PRESENTATION[code];

    return {
      showId: details.showUuid,
      showName: details.showName,
      description: details.description,
      showCode: code,
      abbreviation: code,
      monogram: preset?.monogram ?? code.toUpperCase(),
      accent: preset?.accent ?? DEFAULT_SHOW_ACCENT,
      logoUrl: preset?.logoUrl ?? null,
      tint: preset?.tint ?? DEFAULT_SHOW_TINT,
      cta: preset?.cta ?? DEFAULT_SHOW_CTA
    };
  }

  /** Build the dashboard data structure with live stats from GetDashboardStats. */
  private buildDashboard(stats: DashboardStats): DashboardData {
    const rawFamilyName = this.session.getSessionItem(BaseSessionService.KEY_DISPLAY_NAME) || 'Family';
    const familyName = this.capitalize(rawFamilyName);
    const county = this.session.getSessionItem(CoreService.KEY_FAMILY_FOURH_COUNTY) || '';

    // For family names, extract first/last words to match CommonUtils.initials signature.
    const words = familyName.trim().split(/\s+/).filter(Boolean);
    const first = words[0] ?? '';
    const last = words.length >= 2 ? words[words.length - 1] : '';

    const statCards = this.buildStatCards(stats);

    return {
      profile: {
        name: familyName,
        location: county,
        initials: last ? CommonUtils.initials(first, last) : first.slice(0, 2).toUpperCase()
      },
      greeting: `Hi, ${familyName} family`,
      subtitle: "Here's your livestock show season at a glance — click any card for details",
      alert: null,
      nav: [
        { label: 'Dashboard', icon: 'pi pi-th-large', route: '/family/dashboard' }
      ],
      setupSteps: [],
      stats: statCards
    };
  }

  /** Build stat cards from GetDashboardStats response. */
  private buildStatCards(stats: DashboardStats): StatCard[] {
    const memberCount = stats.familyMemberCount ?? 0;
    const showCount = stats.showCount ?? 0;
    const entryCount = stats.entryCount ?? 0;
    const alertCount = stats.alertCount ?? 0;

    return [
      {
        key: 'members',
        label: 'Family Members',
        value: CommonUtils.pad(memberCount),
        caption: '',
        icon: 'pi pi-users',
        tone: 'info',
        route: '/family/members'
      },
      {
        key: 'shows',
        label: 'Shows Enrolled',
        value: CommonUtils.pad(showCount),
        caption: '',
        icon: 'pi pi-trophy',
        tone: 'success',
        route: '/family/shows'
      },
      {
        key: 'entries',
        label: 'Total Entries',
        value: CommonUtils.pad(entryCount),
        caption: '',
        icon: 'pi pi-list-check',
        tone: 'info',
        // Entries live per show, so the card opens the show picker.
        route: '/family/shows'
      },
      {
        key: 'alerts',
        label: 'Alerts',
        value: CommonUtils.pad(alertCount),
        caption: alertCount > 0 ? 'Action required' : 'No alerts',
        icon: 'pi pi-bell',
        tone: alertCount > 0 ? 'warning' : 'info'
      }
    ];
  }

  /** Verify a Quality Counts number and get its details. Errors with a
   *  `QualityCountNotFoundError` when the number isn't on file.
   *  Typeahead lookup, so it opts out of the global loading overlay — the field
   *  states the outcome with its own "Checking…" chip. */
  getQualityCounts(qcNumber: string): Observable<QualityCount> {
    const params = new HttpParams().set('qcNumber', qcNumber);
    return this.http
      .get<QualityCountResponse>(GET_QUALITY_COUNT_BY_QC_NUMBER, { params, context: skipLoading() })
      .pipe(
        // A real HTTP 404 means the same as the 200 + `success: false` envelope.
        catchError((err: HttpErrorResponse) => (err.status === 404 ? of(null) : throwError(() => err))),
        map(unwrapQualityCount)
      );
  }
}
